import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './AuthProvider'
import type { OperatorRole } from '../api/types'

interface RequireRoleProps {
  role: OperatorRole
  children: ReactNode
}

// Gate component used inside the router: redirects to /login when the
// session is missing, renders a 403 panel when the operator's role is
// below `role`, and renders children otherwise. The bootstrap
// `loading` state shows a calm placeholder so we never flash the
// login page on a refresh of an already-authenticated session.
export function RequireRole({ role, children }: RequireRoleProps) {
  const auth = useAuth()
  const location = useLocation()

  if (auth.status === 'loading') {
    return (
      <div
        className="flex h-full items-center justify-center text-stone-500"
        role="status"
        aria-live="polite"
      >
        Loading…
      </div>
    )
  }
  if (auth.status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  if (!auth.requireRole(role)) {
    return (
      <div className="mx-auto max-w-xl p-12 text-center">
        <h1 className="text-2xl font-semibold text-stone-900">
          Insufficient role
        </h1>
        <p className="mt-3 text-stone-600">
          Your operator role ({auth.operator.role}) is not authorised for
          this page. Contact an administrator if you need {role} access.
        </p>
      </div>
    )
  }
  return <>{children}</>
}
